import type { ReactElement } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger } from "../../../../shared/components/ui/select";

interface PaginationProps {
  readonly currentPage: number;
  readonly totalPages: number;
  readonly totalItems: number;
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly onPageSizeChange: (size: number) => void;
}

/**
 * Generate page numbers for pagination display
 */
function generatePageNumbers(currentPage: number, totalPages: number): Array<number | string> {
  const pageNumbers: Array<number | string> = [];
  const maxPagesToShow = 7;

  if (totalPages <= maxPagesToShow) {
    // Show all pages
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(i);
    }
  } else {
    // Show first, last, and pages around current
    pageNumbers.push(1);

    if (currentPage > 3) {
      pageNumbers.push("ellipsis-start");
    }

    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pageNumbers.push(i);
    }

    if (currentPage < totalPages - 2) {
      pageNumbers.push("ellipsis-end");
    }

    pageNumbers.push(totalPages);
  }

  return pageNumbers;
}

/**
 * Pagination component for bulk title editor
 * Provides page navigation and items-per-page selection
 */
export function Pagination({
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onPageSizeChange,
}: Readonly<PaginationProps>): ReactElement {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const handlePageSizeChange = (value: string) => {
    onPageSizeChange(Number.parseInt(value, 10));
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const pageNumbers = generatePageNumbers(currentPage, totalPages);

  return (
    <div className="flex items-center justify-between px-4 py-3 bg-neutral-900/30 border border-neutral-800 rounded-lg">
      {/* Left: Items count */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-neutral-400">
          Showing {startItem} - {endItem} of {totalItems} titles
        </span>

        {/* Page size selector */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-neutral-400">Show:</span>
          <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
            <SelectTrigger className="w-[80px] h-8">
              <span className="text-sm">{pageSize}</span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="25">25</SelectItem>
              <SelectItem value="50">50</SelectItem>
              <SelectItem value="100">100</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Right: Page navigation */}
      <div className="flex items-center gap-2">
        {/* Previous button */}
        <button
          type="button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-neutral-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 disabled:pointer-events-none disabled:opacity-50 h-8 w-8 p-0"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="sr-only">Previous page</span>
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((page) =>
            typeof page === "string" ? (
              <span key={page} className="px-2 text-neutral-500">
                ...
              </span>
            ) : (
              <button
                key={page}
                type="button"
                onClick={() => onPageChange(page)}
                className={`inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors h-8 w-8 ${
                  currentPage === page
                    ? "bg-lumi-accent text-black hover:bg-lumi-accent/90"
                    : "hover:bg-neutral-800 hover:text-white"
                }`}
              >
                {page}
              </button>
            ),
          )}
        </div>

        {/* Next button */}
        <button
          type="button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
          className="inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors hover:bg-neutral-800 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 disabled:pointer-events-none disabled:opacity-50 h-8 w-8 p-0"
        >
          <ChevronRight className="h-4 w-4" />
          <span className="sr-only">Next page</span>
        </button>
      </div>
    </div>
  );
}
